import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";

const systemPrefersDark = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;

function resolveInitialTheme() {
  const stored = localStorage.getItem("bsp_theme");
  if (stored === "dark" || stored === "light") return stored;
  return systemPrefersDark() ? "dark" : "light";
}

export function applyStoredTheme() {
  const t = resolveInitialTheme();
  document.documentElement.classList.toggle("dark", t === "dark");
  return t;
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState(resolveInitialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("bsp_theme", theme);
  }, [theme]);

  // Follow the OS setting automatically, unless the user has made an explicit choice.
  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      if (!localStorage.getItem("bsp_theme_explicit")) setTheme(e.matches ? "dark" : "light");
    };
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const toggle = () => {
    localStorage.setItem("bsp_theme_explicit", "1");
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  };

  return (
    <Button size="icon" variant="ghost" onClick={toggle} data-testid="theme-toggle"
      aria-label="Toggle theme" className="active:scale-90 transition-transform">
      {theme === "dark"
        ? <Sun className="h-5 w-5 text-amber-400" />
        : <Moon className="h-5 w-5" />}
    </Button>
  );
}
